// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Fragment, type FC } from 'react';
import {
  List,
  ListSubheader,
  Checkbox,
  ListItemButton,
  Collapse,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { useTranslation } from 'react-i18next';
import { formatToMoney } from '@shared/ui-kit/utils/format-to-money';
import { maskIban } from '@shared/advisory/aggregation/utils/bank-utils';

interface ShareStepAccountListProps {
  institutionGroups: any[];
  selectedIbans: string[];
  toggleCheckbox: (iban: string) => void;
  expanded: string[];
  setExpanded: (expanded: any) => void;
}

export const ShareStepAccountList: FC<ShareStepAccountListProps> = ({
  institutionGroups,
  selectedIbans,
  toggleCheckbox,
  expanded,
  setExpanded,
}) => {
  const { t } = useTranslation();

  return (
    <List dense disablePadding>
      {institutionGroups.map(group => {
        const gIbans = group.accounts.map((a: any) => a.iban);
        const isGroupSelected = gIbans.every((i: string) =>
          selectedIbans.includes(i),
        );
        return (
          <Fragment key={group.id}>
            <ListSubheader disableGutters className="!sticky !top-0 !z-10 bg-page">
              <div className="flex items-center bg-page px-4 transition-colors hover:bg-gray-100">
                <div
                  onClick={() =>
                    setExpanded((p: string[]) =>
                      p.includes(group.id)
                        ? p.filter(id => id !== group.id)
                        : [...p, group.id],
                    )
                  }
                  className="cursor-pointer p-1"
                >
                  <AppIcon
                    icon={expanded.includes(group.id) ? 'ArrowDown' : 'ArrowRight'}
                    size="md"
                    className="text-gray-400 hover:bg-gray-100"
                  />
                </div>
                <Checkbox
                  checked={isGroupSelected}
                  onChange={() => {
                    if (isGroupSelected) {
                      gIbans.forEach(
                        (i: string) =>
                          selectedIbans.includes(i) && toggleCheckbox(i),
                      );
                    } else {
                      gIbans.forEach(
                        (i: string) =>
                          !selectedIbans.includes(i) && toggleCheckbox(i),
                      );
                    }
                  }}
                  disableRipple
                  className="!p-1"
                />
                <ListItemButton
                  onClick={() =>
                    setExpanded((p: string[]) =>
                      p.includes(group.id)
                        ? p.filter(id => id !== group.id)
                        : [...p, group.id],
                    )
                  }
                  disableGutters
                  className="!ml-2 !py-3"
                >
                  <img
                    src={group.logo_url ?? ''}
                    alt={group.name}
                    className="mr-3 h-8 w-8 rounded bg-transparent object-contain"
                  />
                  <ListItemText
                    primary={group.name}
                    secondary={t('aggregator.shared-accounts-count', {
                      count: group.accounts.length,
                      ns: 'aggregator',
                    })}
                    primaryTypographyProps={{
                      className: 'text-md text-border-dark !font-medium',
                    }}
                    secondaryTypographyProps={{
                      className: '!text-sm !text-primary-medium !opacity-100',
                    }}
                  />
                </ListItemButton>
              </div>
            </ListSubheader>
            <Collapse in={expanded.includes(group.id)}>
              {group.accounts.map((acc: any) => (
                <ListItem
                  key={acc.iban}
                  disablePadding
                  className="pl-14 pr-6 hover:bg-gray-100"
                >
                  <ListItemButton
                    onClick={() => toggleCheckbox(acc.iban)}
                    disableGutters
                    className="!py-2"
                  >
                    <ListItemIcon className="mr-3 !min-w-0">
                      <Checkbox
                        checked={selectedIbans.includes(acc.iban)}
                        disableRipple
                        className="!p-0"
                      />
                    </ListItemIcon>
                    <div className="flex w-full items-center justify-between text-border-dark">
                      <div className="flex flex-col">
                        <span className="text-md !text-primary-medium !opacity-100">
                          {acc.name || '-'}
                        </span>
                        <span className="!text-[10px] !text-primary-medium !opacity-100">
                          {maskIban(acc.iban, 'first4-last4')}
                        </span>
                      </div>
                      <span className="text-md font-medium !text-primary-medium !opacity-100">
                        {formatToMoney(acc.cash, acc.currency)}
                      </span>
                    </div>
                  </ListItemButton>
                </ListItem>
              ))}
            </Collapse>
          </Fragment>
        );
      })}
    </List>
  );
};
